/**
 * 変更履歴管理サービス
 * LocalStorageに変更履歴を永続化する
 */

import type { ChangeRecord } from '../types';

const STORAGE_KEY = 'rcal_change_history';
const LAST_SYNC_TIME_KEY = 'rcal_last_sync_time';
const MAX_HISTORY_SIZE = 500;
const EXPIRY_DAYS = 7;

interface DumpOptions {
  entityType?: string;
  source?: string;
  limit?: number;
  raw?: boolean;
}

interface ChangeHistoryStats {
  total: number;
  lastHour: number;
  lastDay: number;
  bySource: {
    user: number;
    sync: number;
    pusher: number;
  };
  byEntityType: {
    reservation: number;
    vehicle: number;
    vehicleType: number;
    resource: number;
  };
}

/**
 * 変更履歴をLocalStorageに保存
 */
export function saveChangeToHistory(record: any): void {
  try {
    const history = getChangeHistory();
    const updated = [record, ...history].slice(0, MAX_HISTORY_SIZE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.warn('Failed to save change history:', error);
  }
}

/**
 * 変更履歴をLocalStorageから取得
 */
export function getChangeHistory(): any[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const history = JSON.parse(raw);
    if (!Array.isArray(history)) return [];

    const cutoff = Date.now() - (EXPIRY_DAYS * 24 * 60 * 60 * 1000);

    // 7日以上前のレコードを除外
    return history.filter((record: any) => {
      if (!record || !record.timestamp) return false;
      const recordTime = new Date(record.timestamp).getTime();
      return !isNaN(recordTime) && recordTime > cutoff;
    });
  } catch (error) {
    console.warn('Failed to load change history:', error);
    return [];
  }
}

/**
 * 変更履歴をクリア
 */
export function clearChangeHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear change history:', error);
  }
}

/**
 * 最終同期時刻を保存
 */
export function saveLastSyncTime(isoTime: string): void {
  try {
    localStorage.setItem(LAST_SYNC_TIME_KEY, isoTime);
  } catch (error) {
    console.warn('Failed to save last sync time:', error);
  }
}

/**
 * 最終同期時刻を取得
 */
export function getLastSyncTime(): string | null {
  try {
    return localStorage.getItem(LAST_SYNC_TIME_KEY) || null;
  } catch (error) {
    return null;
  }
}

/**
 * 直近の変更数を取得
 */
export function getRecentChangeCount(minutesAgo: number = 60): number {
  const history = getChangeHistory();
  const cutoff = Date.now() - (minutesAgo * 60 * 1000);

  return history.filter((record: any) => {
    const recordTime = new Date(record.timestamp).getTime();
    return recordTime > cutoff;
  }).length;
}

/**
 * 特定のソースからの変更数を取得
 */
export function getChangeCountBySource(source: string, minutesAgo: number = 60): number {
  const history = getChangeHistory();
  const cutoff = Date.now() - (minutesAgo * 60 * 1000);

  return history.filter((record: any) => {
    if (record.source !== source) return false;
    const recordTime = new Date(record.timestamp).getTime();
    return recordTime > cutoff;
  }).length;
}

/**
 * 重複する変更かどうかをチェック
 */
export function isDuplicateChange(
  history: ChangeRecord[],
  entityType: string,
  entityId: string | number,
  updatedAt: string
): boolean {
  if (!history || !entityType || !entityId || !updatedAt) return false;

  const recentWindow = 60 * 1000; // 1分以内
  const targetTime = new Date(updatedAt).getTime();

  return history.some((record: any) => {
    if (record.entityType !== entityType) return false;
    if (String(record.entityId) !== String(entityId)) return false;

    const recordTime = new Date(record.timestamp).getTime();
    return Math.abs(recordTime - targetTime) < recentWindow;
  });
}

/**
 * 変更履歴をコンソールにダンプ（デバッグ用）
 * ブラウザコンソールから window.__dumpChangeHistory() で呼び出せる
 */
export function dumpChangeHistory(options: DumpOptions = {}): ChangeRecord[] {
  const history = getChangeHistory();
  let filtered: ChangeRecord[] = history;

  if (options.entityType) {
    filtered = filtered.filter((r: any) => r.entityType === options.entityType);
  }
  if (options.source) {
    filtered = filtered.filter((r: any) => r.source === options.source);
  }
  if (options.limit) {
    filtered = filtered.slice(0, options.limit);
  }

  console.group(`📋 変更履歴ダンプ (${filtered.length}/${history.length}件)`);

  if (options.raw) {
    console.log(JSON.parse(JSON.stringify(filtered)));
  } else {
    for (const record of filtered) {
      const time = new Date((record as any).timestamp).toLocaleString('ja-JP');
      const changesStr = ((record as any).changes || []).map((c: any) => {
        const label = c.label || c.field;
        const oldV = c.oldValue ?? c.rawOldValue ?? '(なし)';
        const newV = c.newValue ?? c.rawNewValue ?? '(なし)';
        return `  ${label}: ${JSON.stringify(oldV)} → ${JSON.stringify(newV)}`;
      }).join('\n');

      console.groupCollapsed(
        `%c[${(record as any).source}] %c${(record as any).summary} %c(${time})`,
        'color: #8b5cf6; font-weight: bold',
        'color: inherit',
        'color: #6b7280'
      );
      console.log('ID:', (record as any).id);
      console.log('Entity:', record.entityType, '#', record.entityId);
      console.log('Type:', record.changeType);
      console.log('Source:', record.source);
      console.log('Device:', (record as any).deviceName || (record as any).deviceId);
      if ((record as any).metadata && Object.keys((record as any).metadata).length > 0) {
        console.log('Metadata:', (record as any).metadata);
      }
      if (changesStr) {
        console.log('Changes:\n' + changesStr);
      }
      console.log('Raw record:', record);
      console.groupEnd();
    }
  }

  console.groupEnd();
  return filtered;
}

// グローバルからアクセス可能にする
if (typeof window !== 'undefined') {
  (window as any).__dumpChangeHistory = dumpChangeHistory;
  (window as any).__getChangeHistory = getChangeHistory;
  (window as any).__getChangeHistoryStats = getChangeHistoryStats;
}

/**
 * 変更履歴の統計情報を取得
 */
export function getChangeHistoryStats(): ChangeHistoryStats {
  const history = getChangeHistory();
  const now = Date.now();
  const oneHourAgo = now - (60 * 60 * 1000);
  const oneDayAgo = now - (24 * 60 * 60 * 1000);

  const stats: ChangeHistoryStats = {
    total: history.length,
    lastHour: 0,
    lastDay: 0,
    bySource: {
      user: 0,
      sync: 0,
      pusher: 0,
    },
    byEntityType: {
      reservation: 0,
      vehicle: 0,
      vehicleType: 0,
      resource: 0,
    },
  };

  for (const record of history) {
    const recordTime = new Date((record as any).timestamp).getTime();

    if (recordTime > oneHourAgo) stats.lastHour++;
    if (recordTime > oneDayAgo) stats.lastDay++;

    if (record.source && (stats.bySource as any)[record.source] !== undefined) {
      (stats.bySource as any)[record.source]++;
    }

    if (record.entityType && (stats.byEntityType as any)[record.entityType] !== undefined) {
      (stats.byEntityType as any)[record.entityType]++;
    }
  }

  return stats;
}
